// # Questions Guide Component
import React, { useEffect, useRef, useState } from 'react';
import { RocketOutlined, BookOutlined, ToolOutlined } from '@ant-design/icons';
import DashboardHeader from '../../../components/DashboardHeader';
import Header from '../../../landing/components/header';  // # Import regular header
import DashboardFooter from '../../../components/DashboardFooter';
import { useNavigate } from 'react-router-dom';
import './guide_questions.css';
import { auth, db } from '../../../auth/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import cacheManager from '../../../auth/cache_manager';
import { useAuthStore } from '../../../auth/state';  // # Import auth store

const QuestionsGuide = () => {
  const navigate = useNavigate();
  const sectionRefs = useRef([]);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const { isAuthenticated } = useAuthStore();  // # Get auth state

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (auth.currentUser?.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, []);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (auth.currentUser?.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${auth.currentUser.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  const explanationSection = (
    <div className="gde_qst_explanation-review">
      <h3>How to Efficiently Review the Explanation Section</h3>
      <p className="gde_qst_explanation-intro">
        Reviewing explanations is just as important as answering questions. To maximize retention and avoid wasting time, follow these strategic guidelines.
      </p>

      <div className="gde_qst_review-steps">
        <div className="gde_qst_review-step">
          <h4>I. Focus on Key Concepts</h4>
          <ul>
            <li>Identify the core concept being tested</li>
            <li>Look for patterns in correct and incorrect answers</li>
            <li>Pay attention to high-yield associations</li>
          </ul>
        </div>

        <div className="gde_qst_review-step">
          <h4>II. Adaptive Review Strategy</h4>
          <p>
            Adjust your review depth based on your confidence level:
          </p>
          <ul>
            <li>Correct & Confident: Quick review of key points</li>
            <li>Correct but Unsure: Focus on reinforcing concepts</li>
            <li>Incorrect: Deep dive into explanation and related topics</li>
          </ul>
        </div>

        <div className="gde_qst_review-step">
          <h4>III. Active Learning Approach</h4>
          <ul>
            <li>Create quick summaries in your own words</li>
            <li>Connect concepts to clinical scenarios</li>
            <li>Note common pitfalls and distractors</li>
          </ul>
        </div>
      </div>

      <p className="gde_qst_explanation-conclusion">
        Remember: Quality over quantity. A thorough understanding of fewer questions is more valuable than superficial review of many.
      </p>
    </div>
  );

  return (
    <div className={`gde_qst_page-container ${isDarkMode ? 'gde_qst_dark-mode' : ''}`}>
      {isAuthenticated ? (
        <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      ) : (
        <Header />
      )}

      <div className="gde_qst_content-container">
        <div className="gde_qst_header">
          <div className="gde_qst_header-content">
            <div className="gde_qst_header-text">
              <h1>USMLE Questions Guide</h1>
              <p>Your comprehensive guide to mastering USMLE questions</p>
            </div>
          </div>
        </div>

        <div className="gde_qst_sections">
          {/* Demographics Section */}
          <div className="gde_qst_section">
            <div className="gde_qst_section-header">
              <div className="gde_qst_icon-container">
                <BookOutlined className="gde_qst_section-icon" />
              </div>
              <h2>Understanding Disease Demographics</h2>
            </div>
            
            <div className="gde_qst_content-block">
              <p>
                The USMLE emphasizes classic presentations of diseases. Knowing demographics can help eliminate incorrect options quickly:
              </p>
              
              <div className="gde_qst_disease-list">
                <div className="gde_qst_disease-item pink">
                  <h3>Systemic Lupus Erythematosus (SLE)</h3>
                  <p>Common in young (20–40 years) females. Rare in males or older females (older than 60 years).</p>
                </div>
                <div className="gde_qst_disease-item mint">
                  <h3>Lung Adenocarcinoma</h3>
                  <p>Typically seen in older females and non-smoker males. Exclude it if the patient is a young female or smoker male.</p>
                </div>
                <div className="gde_qst_disease-item blue">
                  <h3>Uterine Fibroids (Leiomyomas)</h3>
                  <p>Estrogen-dependent, affecting premenopausal females. Postmenopausal uterine masses are more likely to be malignant.</p>
                </div>
              </div>

              <div className="gde_qst_info-box">
                <h3>High-Yield Information to Memorize</h3>
                <ul>
                  <li>Classic age and sex of the disease</li>
                  <li>Risk factors (e.g., smoking in head and neck cancers)</li>
                  <li>Pathological/laboratory findings (e.g., low serum ferritin in iron deficiency anemia)</li>
                  <li>Pathognomonic buzzwords/images (e.g., Gram-positive diplococci for Streptococcus pneumoniae)</li>
                  <li>Drug adverse effects (e.g., QT prolongation with macrolides)</li>
                </ul>
                <div className="gde_qst_note">
                  Tools like Pixorize are recommended for pharmacology; Sketchy Pharm is considered outdated.
                </div>
              </div>
            </div>
          </div>

          {/* QBank Section */}
          <div className="gde_qst_section">
            <div className="gde_qst_section-header">
              <div className="gde_qst_icon-container">
                <ToolOutlined className="gde_qst_section-icon" />
              </div>
              <h2>Maximizing QBank Use</h2>
            </div>

            <div className="gde_qst_content-block">
              <div className="gde_qst_strategy-block">
                <h3>1. Treat QBanks as Learning Tools, Not Assessment Tools</h3>
                <div className="gde_qst_subsection">
                  <h4>Study First</h4>
                  <p>Ensure you thoroughly study the subject/system before using UWorld (UW) or Amboss. This strategy is especially critical for Step 1.</p>
                </div>
                <div className="gde_qst_subsection">
                  <h4>Identify Weaknesses</h4>
                  <p>After practicing, analyze areas of difficulty (e.g., strong in CAD but weak in aortic aneurysms). This targeted review helps refine knowledge.</p>
                </div>
              </div>

              <div className="gde_qst_strategy-block">
                <h3>2. Reviewing QBank Questions</h3>
                <div className="gde_qst_question-categories">
                  <div className="gde_qst_category easy">
                    <h4>Easy Questions</h4>
                    <p>If you know the reasoning, skim the learning objective at the bottom of the explanation.</p>
                  </div>
                  <div className="gde_qst_category intermediate">
                    <h4>Intermediate Questions</h4>
                    <p>If unsure or guessed correctly, focus on the explanation for the correct answer only.</p>
                  </div>
                  <div className="gde_qst_category hard">
                    <h4>Hard Questions</h4>
                    <p>For questions you get wrong or don't understand, read the entire explanation, including why incorrect options are wrong.</p>
                  </div>
                </div>

                {explanationSection}
              </div>

              <div className="gde_qst_strategy-block">
                <h3>3. Avoid Copy-Pasting Explanations</h3>
                <div className="gde_qst_example-box">
                  <p className="gde_qst_emphasis">Simplify explanations → Extract key concepts, such as the relationship between Wilson's disease, Fanconi syndrome, and membranous nephropathy:</p>
                  <blockquote>
                    Wilson's disease is associated with Fanconi syndrome. Treatment with penicillamine can lead to membranous nephropathy.
                  </blockquote>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="gde_qst_cta-section">
          <div className="gde_qst_cta-content">
            <div className="gde_qst_cta-icon">
              <RocketOutlined />
            </div>
            <h2>Ready to Excel in Your USMLE Journey?</h2>
            <p>We offer personalized and perfectly-designed mentorship programs for USMLE Step 1 & 2</p>
            <button 
              className="gde_qst_cta-button"
              onClick={() => navigate('/products')}
            >
              Explore Our Programs
            </button>
          </div>
        </div>
      </div>

      <div className="gde_qst_footer-wrapper">
        <DashboardFooter isDarkMode={isDarkMode} />
      </div>
    </div>
  );
};

export default QuestionsGuide;
